import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Image} from '@salesforce/retail-react-app/app/components/shared/ui'
import getCloudinaryVideoUrl from '../../../../cloudinary-media/video'
import useIntersectionObserver from '@salesforce/retail-react-app/app/hooks/use-intersection-observer'
import breakpoints from '../../../../../theme/foundations/breakpoints'

const Video = ({
    eagerLoadImage = false,
    desktopVideo,
    mobileVideo,
    mobileVideoThumbnail,
    desktopVideoThumbnail,
    videoAlt,
    aspectRatio,
    cloudinaryAspectRatio
}) => {
    const videoRef = useRef(null)
    const containerRef = useRef(null)
    const nearViewport = useIntersectionObserver(containerRef, {rootMargin: '400px', useOnce: true})
    const [hasFrame, setHasFrame] = useState(false)
    const desktopPoster = desktopVideoThumbnail || desktopVideo?.replace(/\.mp4(?=\?|$)/i, '.webp')
    const mobilePoster =
        mobileVideoThumbnail || mobileVideo?.replace(/\.mp4(?=\?|$)/i, '.webp') || desktopPoster
    useEffect(() => {
        setHasFrame(false)
        if (nearViewport) videoRef.current?.load()
    }, [nearViewport, desktopVideo, mobileVideo])
    const videoAspectRatio = cloudinaryAspectRatio || aspectRatio?.replaceAll('/', ':')
    const mobileVideoSrc = useMemo(
        () =>
            mobileVideo?.indexOf('ar_') !== -1
                ? mobileVideo
                : getCloudinaryVideoUrl({
                      src: mobileVideo,
                      aspectRatio: videoAspectRatio
                  }),
        [mobileVideo, videoAspectRatio]
    )
    const desktopVideoSrc = useMemo(
        () =>
            desktopVideo?.indexOf('ar_') !== -1
                ? desktopVideo
                : getCloudinaryVideoUrl({
                      src: desktopVideo,
                      aspectRatio: videoAspectRatio
                  }),
        [desktopVideo, videoAspectRatio]
    )

    return (
        <Box ref={containerRef} gridArea="1 / 1" width="100%" height="100%" position="relative">
            <picture style={{visibility: hasFrame ? 'hidden' : 'visible'}}>
                <source media={`(min-width: ${breakpoints.md})`} srcSet={desktopPoster} />
                <Image
                    src={mobilePoster}
                    alt={videoAlt || ''}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                    aspectRatio={aspectRatio || 'unset'}
                    loading={eagerLoadImage ? 'eager' : 'lazy'}
                    fetchPriority={eagerLoadImage ? 'high' : undefined}
                />
            </picture>
            <video
                ref={videoRef}
                preload="none"
                autoPlay={nearViewport}
                onLoadedData={() => setHasFrame(true)}
                onError={() => setHasFrame(false)}
                muted
                alt={videoAlt}
                loop
                playsInline
                style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    aspectRatio: aspectRatio || 'unset'
                }}
            >
                <source
                    src={nearViewport ? desktopVideoSrc : undefined}
                    media={`(min-width: ${breakpoints.md})`}
                />
                <source src={nearViewport ? mobileVideoSrc || desktopVideoSrc : undefined} />
            </video>
        </Box>
    )
}

Video.propTypes = {
    eagerLoadImage: PropTypes.bool,
    desktopVideo: PropTypes.string.isRequired,
    mobileVideo: PropTypes.string,
    mobileVideoThumbnail: PropTypes.string,
    desktopVideoThumbnail: PropTypes.string,
    videoAlt: PropTypes.string,
    videoEnableSound: PropTypes.bool,
    aspectRatio: PropTypes.string,
    cloudinaryAspectRatio: PropTypes.string,
    videoDisableAutoPlay: PropTypes.bool
}

export default Video
